import { nanoid } from 'nanoid'
import type { ConceptMapData, TeachingForm } from '../types'
import type { Finding } from '../types/findings'

export function generateFindings(
  before: ConceptMapData | undefined,
  after: ConceptMapData,
  sessionId: string,
  teachingForm: TeachingForm
): Finding[] {
  const findings: Finding[] = []
  const timestamp = new Date().toISOString()
  const beforeNodes = before?.nodes ?? []
  const beforeEdges = before?.edges ?? []
  const beforeNodeIds = new Set(beforeNodes.map(n => n.id))
  const beforeEdgeIds = new Set(beforeEdges.map(e => e.id))

  // New nodes
  for (const node of after.nodes) {
    if (!beforeNodeIds.has(node.id)) {
      findings.push({
        id: nanoid(),
        type: 'node_added',
        sessionId,
        teachingForm,
        timestamp,
        data: {
          nodeId: node.id,
          label: node.label,
          bloomLevel: node.bloomLevel,
        },
        status: 'pending',
      })
    }
  }

  // Bloom level changes
  for (const node of after.nodes) {
    if (beforeNodeIds.has(node.id)) {
      const beforeNode = beforeNodes.find(n => n.id === node.id)
      if (beforeNode && beforeNode.bloomLevel !== node.bloomLevel) {
        findings.push({
          id: nanoid(),
          type: 'bloom_updated',
          sessionId,
          teachingForm,
          timestamp,
          data: {
            nodeId: node.id,
            label: node.label,
            bloomLevel: node.bloomLevel,
            previousBloomLevel: beforeNode.bloomLevel,
          },
          status: 'pending',
        })
      }
    }
  }

  // New edges
  for (const edge of after.edges) {
    if (!beforeEdgeIds.has(edge.id)) {
      findings.push({
        id: nanoid(),
        type: 'edge_added',
        sessionId,
        teachingForm,
        timestamp,
        data: {
          edgeSource: edge.source,
          edgeTarget: edge.target,
          relationType: edge.relationType,
          label: edge.label,
        },
        status: 'pending',
      })
    }
  }

  return findings
}
